import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginUser, clearError } from '../store/authSlice';
import Button from 'shared-ui/Button';
import './Login.css';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    try {
      await dispatch(loginUser({ email, password })).unwrap();
    } catch (error) {
      // Error is handled by Redux state
      console.error('Login failed:', error);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    // Clear error when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  return (
    <div className="login-container">
      {/* Left Panel - Orange Background */}
      <div className="left-panel">
        <div className="left-content">
          {/* Logo */}
          <div className="logo-container">
          <img src="/assets/logo-circle-white.svg" alt="Penny Appeal GMS Logo" className="w-[80px] h-[80px]" width={80} height={80} />
          </div>
          
          {/* Welcome Text */}
          <div className="welcome-content">
            <h1 className="welcome-title">
              Welcome to Penny Appeal Australia's Admin Panel
            </h1>
            <p className="welcome-description">
              Here you can manage users, update content, track activity, and control all settings of your website from one place.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="right-panel">
        <div className="form-container">
          {/* Header Logo */}
          <div className="form-logo">
            <img src="/assets/logo.png" alt="Penny Appeal GMS Logo" className="w-[210px] h-[40px]" width={210} height={40} />
          </div>

          {/* Form Header */}
          <div className="form-header">
            <h2 className="form-title">Log in to your account</h2>
            <p className="form-subtitle">Welcome back, please enter your details.</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Error Message */}
            {error && (
              <div className="error-message">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{error}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => dispatch(clearError())}
                    className="text-red-600 hover:text-red-800 focus:outline-none"
                    aria-label="Dismiss error"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                className="form-input"
                required
                disabled={isLoading}
                placeholder="Enter your email"
              />
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  disabled={isLoading}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              disabled={isLoading || !email.trim() || !password.trim()}
              loading={isLoading}
              className="w-full rounded-[20px] h-[38px]"
              variant="primary"
            >
              {isLoading ? 'Logging in...' : 'Log in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
